/**
 * @internal
 */
import { useCallback, useRef, useState } from 'react';
import { useUpdateEffect } from '@polestar/component-warehouse-react/hooks/useUpdateEffect';
/**
 * Used when multiple focusable components exist inside a component and where the focus events
 * are placed onto the container.
 *
 * `useFocusTriggers` is meant for atoms and `useContainerFocusTriggers` for molecules or organisms
 */
export const useContainerFocusTriggers = ({ name, fakeFocus = false, onBlur, onFocus, onPointerDown, onPointerUp, }) => {
    const [internalFakeFocus, setInternalFakeFocus] = useState(false);
    const [focused, setFocused] = useState(false);
    const previousFocus = useRef(false);
    const internalFocused = focused || fakeFocus || internalFakeFocus;
    /**
     *  We need to ref these functions otherwise the onblur/onfocus will trigger when the function change.
     *  We had it in a useEffect before, but then that might trigger AFTER the useUpdateEffect below.
     *  So we just update the ref in the loop itself.
     */
    const onFocusRef = useRef(onFocus);
    onFocusRef.current = onFocus;
    const onBlurRef = useRef(onBlur);
    onBlurRef.current = onBlur;
    /**
     * Fake the blur / focus with an effect
     */
    useUpdateEffect(() => {
        const timeout = setTimeout(() => {
            var _a, _b;
            if (previousFocus.current !== internalFocused) {
                if (internalFocused) {
                    (_a = onFocusRef.current) === null || _a === void 0 ? void 0 : _a.call(onFocusRef, name);
                }
                else {
                    (_b = onBlurRef.current) === null || _b === void 0 ? void 0 : _b.call(onBlurRef, name);
                }
                previousFocus.current = internalFocused;
            }
        }, 20);
        return () => {
            clearTimeout(timeout);
        };
    }, [internalFocused, name]);
    /**
     * These are the events that get put on the element
     */
    const handleBlur = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setFocused(false);
        }
    }, []);
    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);
    /**
     * When a user clicks but doesn't let go, there will be a blur event, but no focus event yet.
     * So if we are inside the containing element, we fake the focus until we get a mouse up event.
     *
     * If we don't do this there is a blur even triggered for people who have a slow click ;)
     */
    const handlePointerDown = useCallback((e) => {
        setInternalFakeFocus(true);
        onPointerDown === null || onPointerDown === void 0 ? void 0 : onPointerDown(e);
    }, [onPointerDown]);
    const handlePointerUp = useCallback((e) => {
        // This is needed, otherwise it gets triggered before the new focus event is triggered
        setTimeout(() => {
            setInternalFakeFocus(false);
        }, 0);
        onPointerUp === null || onPointerUp === void 0 ? void 0 : onPointerUp(e);
    }, [onPointerUp]);
    const focusBlurEvents = {
        onBlur: handleBlur,
        onFocus: handleFocus,
        onPointerDown: handlePointerDown,
        onPointerUp: handlePointerUp,
    };
    return {
        realFocus: focused,
        focused: internalFocused,
        setFocused,
        focusBlurEvents,
    };
};
